import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const groupConversationsByMonth = (conversations) => {
  const map = new Map();
  for (const c of conversations) {
    const monthYear = c.monthYear;
    if (!map.has(monthYear)) {
      map.set(monthYear, []);
    }
    map.get(monthYear).push(c);
  }
  return map;
};

const ConversationTable = ({ conversations }) => {
  const [selectedConversation, setSelectedConversation] = useState(null);

  const handleRowClick = (conversation) => {
    setSelectedConversation(conversation);
  };

  const handleCloseDialog = () => {
    setSelectedConversation(null);
  };

  const messageStyles = {
    user: {
      backgroundColor: '#1e3a5f', // Dark blue for user messages
      padding: '10px',
      borderRadius: '10px',
      marginBottom: '10px',
      maxWidth: '80%',
      alignSelf: 'flex-end',
      color: '#ffffff',
    },
    chatgpt: {
      backgroundColor: '#2c2c2e', // Dark gray for ChatGPT messages
      padding: '10px',
      borderRadius: '10px',
      marginBottom: '10px',
      maxWidth: '80%',
      alignSelf: 'flex-start',
      color: '#ffffff',
    },
  };

  const groupedConversations = groupConversationsByMonth(conversations);

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: 'grey.700',
              }}
            >
              <TableCell style={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Title</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...groupedConversations.keys()].map((monthYear) => (
              <React.Fragment key={monthYear}>
                {/* Month header */}
                <TableRow
                  sx={{
                    bgcolor: (theme) => theme.palette.divider,
                  }}
                >
                  <TableCell colSpan={2} style={{ fontWeight: 'bold' }}>
                    {monthYear}
                  </TableCell>
                </TableRow>
                {/* Conversations for this month */}
                {groupedConversations.get(monthYear).map((conversation) => (
                  <TableRow
                    key={conversation.conversationId}
                    onClick={() => handleRowClick(conversation)}
                    hover
                  >
                    <TableCell>
                      {new Date(conversation.updatedDate).toLocaleString()}
                    </TableCell>
                    <TableCell>{conversation.title}</TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog for displaying conversation details */}
      <Dialog
        open={!!selectedConversation}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedConversation?.title}
          <IconButton
            component="a"
            title="Open in ChatGPT"
            href={`https://chatgpt.com/c/${selectedConversation?.conversationId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            <OpenInNewIcon sx={{ color: '#1e90ff' }} />{' '}
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            {selectedConversation?.messages.map((msg, index) => (
              <Box
                key={index}
                style={
                  msg.author === 'user'
                    ? messageStyles.user
                    : messageStyles.chatgpt
                }
              >
                <Typography variant="body1">
                  <strong>{msg.author === 'user' ? 'You' : 'ChatGPT'}:</strong>
                </Typography>
                {msg.author === 'ChatGPT' ? (
                  <ReactMarkdown>{msg.text}</ReactMarkdown> // Render markdown for ChatGPT messages
                ) : (
                  <Typography variant="body1">{msg.text}</Typography> // Regular text for user messages
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConversationTable;
